import { CHART_FONT_FAMILY, DEFAULT_CHART_COLORS } from './chart-setup.js';
/** Pixels a bar's value label sits past the bar's end, or a line's end label
 *  sits past its last point (spec §7.2-7.3). Exported so tests can compute
 *  the exact padding a plugin should reserve without duplicating the
 *  constant. */
export const VALUE_LABEL_GAP = 5;
export const END_LABEL_GAP = 8;
/** Matches the muted tick-label size `ChartCanvas.buildConfig` already uses. */
export const LABEL_FONT_SIZE = 11;
const ZERO_PADDING = { top: 0, right: 0, bottom: 0, left: 0 };
/** Reads whatever shape a padding option currently holds — the uniform
 *  number `ChartCanvas.buildConfig` starts every chart at, or an already-set
 *  TRBL object — into one. */
function readPadding(padding) {
    if (typeof padding === 'number')
        return { top: padding, right: padding, bottom: padding, left: padding };
    const p = (padding ?? {});
    return { top: p.top ?? 0, right: p.right ?? 0, bottom: p.bottom ?? 0, left: p.left ?? 0 };
}
/** Sets `chart.options.layout.padding` to `max(base, extra)` per side.
 *
 *  `base` must be the padding captured once, in a `beforeInit` hook, before
 *  either plugin ever touched it — never the chart's *current* padding. A
 *  legend click calls `chart.update()` on the same live Chart
 *  instance, which re-runs `beforeLayout` with a series now hidden; maxing
 *  against the current value would leave the padding stuck at whatever the
 *  widest label ever needed, even after that label's series is hidden and
 *  a narrower one no longer needs nearly as much room. Maxing against the
 *  fixed base instead means the padding this pass computes is exactly what
 *  the *currently visible* labels need — it can shrink as readily as it
 *  can grow.
 *
 *  `ChartCanvas.buildConfig` sets `layout: { padding: 0 }` unconditionally,
 *  so a chart with nothing to draw past its plot area keeps that literal
 *  zero — this is only ever called from a `beforeLayout` hook a chart's
 *  `plugins` array carries when it actually has labels to draw outside it
 *  (spec §7.2-7.3). Chart.js reads `options.layout.padding` fresh on every
 *  layout pass (`_updateLayout` calls `notifyPlugins('beforeLayout', …)`
 *  before `layouts.update`), so a change made here takes effect immediately. */
function applyPadding(chart, base, extra) {
    const next = {
        top: Math.max(base.top, extra.top ?? 0),
        right: Math.max(base.right, extra.right ?? 0),
        bottom: Math.max(base.bottom, extra.bottom ?? 0),
        left: Math.max(base.left, extra.left ?? 0)
    };
    chart.options.layout = { ...chart.options.layout, padding: next };
}
/**
 * Pushes a sequence of positions apart to at least `minGap` px apart while
 * preserving their given order (spec §7.2-7.3's end-label collision
 * avoidance), optionally kept within `[bounds.minY, bounds.maxY]`.
 *
 * A forward pass pushes each position down far enough to clear the one
 * before it. The last position is then clamped to `bounds.maxY`, if given,
 * so up to `END_LABEL_MAX_SERIES` labels can never cascade past the bottom
 * of the chart. A backward pass follows, pulling each earlier position back
 * up as far as it can go without re-violating the gap it now owes the one
 * after it — undoing that clamp's knock-on effect on the labels above it —
 * except the very first position is never pulled above `bounds.minY`, if
 * one is given, which would run it off the top instead.
 *
 * Three already-separated positions (`[10, 50, 90]`) come back unchanged —
 * with no bounds tightened, both passes only ever fire on a genuine
 * collision.
 */
export function layoutEndLabels(ys, minGap = 12, bounds = {}) {
    const result = [...ys];
    const last = result.length - 1;
    for (let i = 1; i < result.length; i++) {
        result[i] = Math.max(result[i], result[i - 1] + minGap);
    }
    if (last >= 0 && bounds.maxY !== undefined) {
        result[last] = Math.min(result[last], bounds.maxY);
    }
    for (let i = last - 1; i >= 0; i--) {
        let value = Math.min(result[i], result[i + 1] - minGap);
        if (i === 0 && bounds.minY !== undefined)
            value = Math.max(value, bounds.minY);
        result[i] = value;
    }
    return result;
}
/** Series 1 reads as the chart's lead value; every other series is
 *  scaffolding around it, same as an axis tick (spec §7.2-7.3). */
function seriesLabelColor(datasetIndex, colors) {
    return datasetIndex === 0 ? colors.ink : colors.muted;
}
/** Minimum clear space, in px, between two neighbouring value labels. */
export const VALUE_LABEL_MIN_SPACE = 4;
/** True when any two value labels, laid out where `valueLabelPlugin` would
 *  draw them, would overlap along the category axis. */
function valueLabelsCollide(chart, view, ctx) {
    const spans = [];
    view.series.forEach((series, datasetIndex) => {
        if (!chart.isDatasetVisible(datasetIndex))
            return;
        chart.getDatasetMeta(datasetIndex).data.forEach((element, index) => {
            const value = series.values[index];
            const display = series.displays[index];
            if (value === null || value === undefined || display === undefined)
                return;
            const props = element.getProps(['x', 'y', 'horizontal'], true);
            // Horizontal bars put each label on its own row past the bar's end;
            // only vertical bars line their labels up side by side.
            if (props.horizontal)
                return;
            const centre = props.x;
            const extent = ctx.measureText(display).width;
            // The label's height on the value axis, so two labels at very
            // different heights (a tall bar next to a short one) don't count.
            const offset = props.y;
            spans.push({ start: centre - extent / 2, end: centre + extent / 2, offset });
        });
    });
    spans.sort((a, b) => a.start - b.start);
    for (let i = 1; i < spans.length; i++) {
        const a = spans[i - 1];
        const b = spans[i];
        const sameRow = Math.abs(a.offset - b.offset) < LABEL_FONT_SIZE + VALUE_LABEL_GAP;
        if (sameRow && b.start < a.end + VALUE_LABEL_MIN_SPACE)
            return true;
    }
    return false;
}
/**
 * Prints each bar's preformatted value past the bar's end (spec §7.2). Only
 * ever included in a chart's `plugins` array when `showValueLabels(view)`
 * holds — few enough categories and series that the labels stay legible and
 * don't collide.
 *
 * A vertical bar's label sits above its end (below, for a negative value —
 * away from zero either way); a horizontal bar's sits past its end along the
 * x axis, right for a positive value and left for a negative one. A series
 * the legend has hidden draws no label — there is no bar to sit past.
 */
export function valueLabelPlugin(view, fontFamily = CHART_FONT_FAMILY, colors = DEFAULT_CHART_COLORS) {
    // Captured once, before this plugin ever touches the chart's padding —
    // `beforeInit` always fires before the first `beforeLayout` — so every
    // later `beforeLayout` recomputes from this fixed point rather than from
    // whatever a previous pass grew it to (see `applyPadding`). One plugin
    // instance is only ever attached to one chart for that chart's whole
    // life, so a plain closure variable is exactly "per chart".
    let base;
    return {
        id: 'tf-value-labels',
        beforeInit(chart) {
            base = readPadding(chart.options.layout?.padding);
        },
        // Reserves room for the labels this plugin is about to draw outside the
        // plot area, before Chart.js lays it out (spec §7.2) — otherwise a label
        // past a chart's last bar (e.g. a horizontal bar's widest value) gets
        // clipped by the canvas edge.
        beforeLayout(chart) {
            const horizontal = chart.options.indexAxis === 'y';
            const { ctx } = chart;
            ctx.save();
            ctx.font = `${LABEL_FONT_SIZE}px ${fontFamily}`;
            let positiveWidth = 0;
            let negativeWidth = 0;
            let hasPositive = false;
            let hasNegative = false;
            view.series.forEach((series, datasetIndex) => {
                if (!chart.isDatasetVisible(datasetIndex))
                    return;
                series.values.forEach((value, index) => {
                    const display = series.displays[index];
                    if (value === null || value === undefined || display === undefined)
                        return;
                    const width = ctx.measureText(display).width;
                    if (value < 0) {
                        hasNegative = true;
                        negativeWidth = Math.max(negativeWidth, width);
                    }
                    else {
                        hasPositive = true;
                        positiveWidth = Math.max(positiveWidth, width);
                    }
                });
            });
            ctx.restore();
            if (horizontal) {
                applyPadding(chart, base ?? ZERO_PADDING, {
                    right: hasPositive ? positiveWidth + VALUE_LABEL_GAP : 0,
                    left: hasNegative ? negativeWidth + VALUE_LABEL_GAP : 0
                });
            }
            else {
                applyPadding(chart, base ?? ZERO_PADDING, {
                    top: hasPositive ? LABEL_FONT_SIZE + VALUE_LABEL_GAP : 0,
                    bottom: hasNegative ? LABEL_FONT_SIZE + VALUE_LABEL_GAP : 0
                });
            }
        },
        afterDatasetsDraw(chart) {
            const { ctx } = chart;
            ctx.save();
            ctx.font = `${LABEL_FONT_SIZE}px ${fontFamily}`;
            // `showValueLabels` decides from counts alone, before any pixel exists.
            // At a narrow width the labels can still run into each other
            // ("5.0%4.9%5.1%"), so measure them where they would land and draw none
            // when any two overlap: the axis and tooltip still carry every value,
            // and a row of fused numbers carries none.
            if (valueLabelsCollide(chart, view, ctx)) {
                ctx.restore();
                return;
            }
            view.series.forEach((series, datasetIndex) => {
                if (!chart.isDatasetVisible(datasetIndex))
                    return;
                const meta = chart.getDatasetMeta(datasetIndex);
                ctx.fillStyle = seriesLabelColor(datasetIndex, colors);
                meta.data.forEach((element, index) => {
                    const value = series.values[index];
                    const display = series.displays[index];
                    if (value === null || value === undefined || display === undefined)
                        return;
                    const bar = element;
                    const props = bar.getProps(['x', 'y', 'horizontal'], true);
                    const x = props.x;
                    const y = props.y;
                    const horizontal = props.horizontal;
                    const negative = value < 0;
                    if (horizontal) {
                        ctx.textAlign = negative ? 'right' : 'left';
                        ctx.textBaseline = 'middle';
                        ctx.fillText(display, x + (negative ? -VALUE_LABEL_GAP : VALUE_LABEL_GAP), y);
                    }
                    else {
                        ctx.textAlign = 'center';
                        ctx.textBaseline = negative ? 'top' : 'bottom';
                        ctx.fillText(display, x, y + (negative ? VALUE_LABEL_GAP : -VALUE_LABEL_GAP));
                    }
                });
            });
            ctx.restore();
        }
    };
}
/**
 * Prints each line's series name and final value at its right end (spec
 * §7.2-7.3). Only ever included in a chart's `plugins` array when
 * `showEndLabels(view)` holds — few enough series that the endpoints stay
 * apart.
 *
 * Labels are sorted top-to-bottom by their natural pixel position before
 * `layoutEndLabels` nudges apart any that collide, so a nudge never crosses
 * two lines' labels past each other. A series the legend has hidden draws no
 * label — there is no line to sit past.
 */
export function endLabelPlugin(view, fontFamily = CHART_FONT_FAMILY, colors = DEFAULT_CHART_COLORS) {
    /** The label text each visible series would draw, and the index of its
     *  last (labelled) point — shared between the layout and draw hooks so
     *  neither repeats the other's filtering. */
    function endLabelTexts(chart) {
        const texts = [];
        view.series.forEach((series, datasetIndex) => {
            if (!chart.isDatasetVisible(datasetIndex))
                return;
            const lastIndex = series.values.length - 1;
            if (lastIndex < 0)
                return;
            const value = series.values[lastIndex];
            const display = series.displays[lastIndex];
            if (value === null || value === undefined || display === undefined)
                return;
            texts.push(`${series.label} ${display}`);
        });
        return texts;
    }
    // Captured once, before this plugin ever touches the chart's padding —
    // see `valueLabelPlugin`'s identical `base` for why.
    let base;
    return {
        id: 'tf-end-labels',
        beforeInit(chart) {
            base = readPadding(chart.options.layout?.padding);
        },
        // Reserves room on the right for the widest end label this plugin is
        // about to draw past the last point of every visible line (spec §7.3) —
        // otherwise a label past the chart's right edge gets clipped by the
        // canvas edge (observed on the weekly-trend chart: "Actual hours 1,026"
        // rendered as "Actu…").
        beforeLayout(chart) {
            const { ctx } = chart;
            ctx.save();
            ctx.font = `${LABEL_FONT_SIZE}px ${fontFamily}`;
            const maxWidth = endLabelTexts(chart).reduce((max, text) => Math.max(max, ctx.measureText(text).width), 0);
            ctx.restore();
            // Always applied, even when `maxWidth` is 0 (every series hidden) —
            // otherwise the padding a previously-wider selection of visible
            // series needed would stay stuck at that old value forever.
            applyPadding(chart, base ?? ZERO_PADDING, { right: maxWidth > 0 ? maxWidth + END_LABEL_GAP : 0 });
        },
        afterDatasetsDraw(chart) {
            const entries = [];
            view.series.forEach((series, datasetIndex) => {
                if (!chart.isDatasetVisible(datasetIndex))
                    return;
                const lastIndex = series.values.length - 1;
                if (lastIndex < 0)
                    return;
                const value = series.values[lastIndex];
                const display = series.displays[lastIndex];
                if (value === null || value === undefined || display === undefined)
                    return;
                const meta = chart.getDatasetMeta(datasetIndex);
                const element = meta.data[lastIndex];
                if (!element)
                    return;
                const point = element;
                const props = point.getProps(['x', 'y'], true);
                entries.push({
                    datasetIndex,
                    x: props.x,
                    y: props.y,
                    text: `${series.label} ${display}`
                });
            });
            if (entries.length === 0)
                return;
            // Top-to-bottom, so a nudge only ever pushes a lower label further
            // down — never past a label that was above it. Clamped to the plot
            // area's bottom edge so up to END_LABEL_MAX_SERIES colliding labels
            // can never cascade past it.
            entries.sort((a, b) => a.y - b.y);
            const ys = layoutEndLabels(entries.map((entry) => entry.y), 12, { maxY: chart.chartArea.bottom });
            const x = Math.max(...entries.map((entry) => entry.x));
            const { ctx } = chart;
            ctx.save();
            ctx.font = `${LABEL_FONT_SIZE}px ${fontFamily}`;
            ctx.textAlign = 'left';
            ctx.textBaseline = 'middle';
            entries.forEach((entry, index) => {
                ctx.fillStyle = seriesLabelColor(entry.datasetIndex, colors);
                ctx.fillText(entry.text, x + END_LABEL_GAP, ys[index]);
            });
            ctx.restore();
        }
    };
}
//# sourceMappingURL=chart-labels.js.map